import type { AuthenticatedClientUser } from '@src/authentication/AuthenticatedClientUser';
import type { AuthenticatedUser } from '@src/authentication/AuthenticatedUser';
import { AuthenticationClient } from '@src/authentication/AuthenticationClient';
import { AuthenticationStorageEvents } from '@src/common/AuthenticationStorage';
import { EventEmitter } from '@src/common/EventEmitter';
import { useConfigureAlerting } from '@src/observability/useAlerting';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { AuthenticationContext } from './AuthenticationContext';

export const AuthenticationContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { configureActiveUser } = useConfigureAlerting();
  const [user, setUser] = useState<
    AuthenticatedUser | AuthenticatedClientUser | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      configureActiveUser(user);
    }
  }, [configureActiveUser, user]);

  const fetchAuthenticatedUser = async () => {
    const user = await AuthenticationClient.me();
    setUser(user);
  };

  const clearAuthenticatedUser = (_: Event) => {
    setUser(null);
  };

  const logout = () => {
    setUser(null);
    void AuthenticationClient.logout();
  };

  useEffect(() => {
    setIsLoading(true);
    const refreshSession = async () => {
      await fetchAuthenticatedUser();
      setIsLoading(false);
    };
    void refreshSession();
  }, []);

  useEffect(() => {
    EventEmitter.listen(
      AuthenticationStorageEvents.SetAccessToken,
      fetchAuthenticatedUser,
    );
    EventEmitter.listen(
      AuthenticationStorageEvents.RemoveAccessToken,
      clearAuthenticatedUser,
    );

    return () => {
      EventEmitter.removeListener(
        AuthenticationStorageEvents.SetAccessToken,
        fetchAuthenticatedUser,
      );
      EventEmitter.removeListener(
        AuthenticationStorageEvents.RemoveAccessToken,
        clearAuthenticatedUser,
      );
    };
  }, []);

  return (
    <AuthenticationContext.Provider value={{ user, isLoading, logout }}>
      {children}
    </AuthenticationContext.Provider>
  );
};
